import * as React from 'react';
import {useState} from 'react';
import {Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography} from "@mui/material";
import {
  FLOUR_EXCLUSION_UNITS,
  GENERAL_DRY_EXCLUSION_UNITS,
  getUnitLabel,
  LIQUID_EXCLUSION_UNITS,
  unitCups,
  unitGrams,
  unitLiters,
  unitMillis,
  unitPercent
} from "../shared/Units";
import {UnitSelector} from "../shared/UnitSelector";
import {PrefermentTypeSelector} from "../shared/PrefermentTypeSelector";
import {DecimalFormField} from "../shared/DecimalFormField";
import {isYeastPreferment, noPreferment} from "../shared/Types";
import {QuantityIngredientCollectionForm} from "../shared/QuantityIngredientCollectionForm";

function WeightPrefermentForm(props) {
  const {visible, formData, setFormData, includePrefermentValue, includePrefermentChange} = props
  const [starterPreferment, setStarterPreferment] = useState(true)

  const excludeUnitsYeast = [unitPercent, unitCups, unitLiters, unitMillis]
  const excludeUnitsStarter = [unitPercent, unitCups, unitLiters, unitMillis]

  function onPrefermentTypeChange(value) {
    setStarterPreferment(!isYeastPreferment(value))
    setFormData({...formData, prefermentType: value})
  }

  function updateFlourData(updatedFlourData) {
    setFormData({
      ...formData,
      flour: updatedFlourData
    })
  }

  function updateLiquidData(updatedLiquidData) {
    setFormData({
      ...formData,
      liquid: updatedLiquidData
    })
  }

  function updateMixinData(updatedMixinData) {
    setFormData({
      ...formData,
      mixin: updatedMixinData
    })
  }

  function onDecimalChange(fieldName, floatValue) {
    setFormData({...formData, [fieldName]: floatValue})
  }

  function onUnitChange(id, value) {
    setFormData({...formData, [id]: value})
  }

  if (!visible) {
    return null
  }

  function renderPrefermentType() {
    return (
    <Grid item container direction={'row'} marginLeft={0.25} marginTop={2} alignItems={'flex-start'} columns={{xs: 4, sm: 8, md: 12}}>
      <Grid item xs={4} sm={6} md={8}>
        <PrefermentTypeSelector id={'weight-pf'} prefermentType={formData.prefermentType} onValueChange={onPrefermentTypeChange} excludeTypes={[noPreferment]}/>
      </Grid>
    </Grid>
    )
  }

  function onLeaveningTypeChange(isStarter) {
    setStarterPreferment(isStarter)
    const fieldNameToClear = isStarter ? 'yeastAmount' : 'starterAmount'
    setFormData({...formData, [fieldNameToClear]: 0})
  }

  function renderLeavening() {
    const leaveningLabel = `Starter ${getUnitLabel(formData.starterUnit)}`
    return (
      <div>
        <Divider textAlign={'right'} sx={{marginTop: 2, width: '90%'}}><Typography
                sx={{fontSize: 12, fontWeight: 350}}
                gutterBottom>Leavening</Typography></Divider>
        <Grid item container direction={'column'} marginTop={-2}>
          <FormControl>
            <RadioGroup aria-labelledby="prefermentLeavening" row
                        defaultValue={'Y'}
                        sx={{marginLeft: 0}}
                        value={starterPreferment ? 'Y' : 'N'}
                        onChange={(evt) => onLeaveningTypeChange(evt.target.value === 'Y')}>
              <FormControlLabel sx={{marginLeft: 0.15, marginRight: 0}} value={'Y'}
                                control={<Radio size={'small'} color={'primary'}/>}
                                label={<Typography sx={{fontSize: 14}}>Starter</Typography>}/>
              <FormControlLabel sx={{marginLeft: 0, marginRight: 0}} value={'N'}
                                control={<Radio size={'small'} color={'primary'}/>}
                                label={<Typography sx={{fontSize: 14}}>Dry Yeast</Typography>}/>
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item container direction={'column'} rowSpacing={1} alignItems={'flex-start'} marginBottom={1}>
          {starterPreferment ? (
          <Grid item container direction={'row'} spacing={1} columns={{xs: 4, sm: 8, md: 12}}>
            <Grid item xs={2} sm={2} md={2.5}>
              <DecimalFormField value={formData.starterAmount} size={'small'}
                                name={'starterAmount'} label={leaveningLabel}
                                onChange={onDecimalChange}/>
            </Grid>
            <Grid item xs={0.5} sm={0.75} md={1.5}>
              <UnitSelector id={'starterUnit'} unitValue={formData.starterUnit}
                            onValueChange={onUnitChange} excludeValues={excludeUnitsStarter}/>
            </Grid>
            <Grid item xs={0.75} sm={1} md={2.5} marginLeft={2.5}>
              <DecimalFormField value={formData.starterHydrationPct} size={'small'}
                                name={'starterHydrationPct'} label={'H%'}
                                onChange={onDecimalChange}/>
            </Grid>
          </Grid> ) : (
          <Grid item container direction={'row'} spacing={1} columns={{xs: 4, sm: 8, md: 12}}>
            <Grid item xs={2} sm={2} md={2.5}>
              <DecimalFormField value={formData.yeastAmount} size={'small'}
                                name={'yeastAmount'} label={`Yeast ${getUnitLabel(formData.yeastUnit)}`}
                                onChange={onDecimalChange}/>
            </Grid>
            <Grid item xs={0.5} sm={0.75} md={1.5}>
              <UnitSelector id={'yeastUnit'} unitValue={formData.yeastUnit}
                            onValueChange={onUnitChange} excludeValues={excludeUnitsYeast}/>
            </Grid>
          </Grid> )}
        </Grid>
      </div>
    )
  }

  return (
    <div>
      <Grid item container direction={'column'} marginTop={-2}>
        <FormControl>
          <RadioGroup aria-labelledby="prefermentStyle" row
                      defaultValue={'N'}
                      sx={{marginLeft: 0}}
                      value={includePrefermentValue ? 'Y' : 'N'}
                      onChange={(evt) => includePrefermentChange(evt.target.value === 'Y')}>
            <FormControlLabel sx={{marginLeft: 0.15, marginRight: 0}} value={'N'}
                              control={<Radio size={'small'} color={'primary'}/>}
                              label={<Typography sx={{fontSize: 14}}>Simple ingredient of Main</Typography>}/>
            <FormControlLabel sx={{marginLeft: 0, marginRight: 0}} value={'Y'}
                              control={<Radio size={'small'} color={'primary'}/>}
                              label={<Typography sx={{fontSize: 14}}>Detailed</Typography>}/>
          </RadioGroup>
        </FormControl>
      </Grid>
      {includePrefermentValue ? (
      <Grid item container direction={'column'}>
        <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
          <Typography sx={{fontSize: 12, fontWeight: 350}}>Detailed Preferment</Typography>
        </Divider>
        {renderPrefermentType()}
        {renderLeavening()}
        <Divider textAlign={'right'} sx={{marginTop: 2, width: '90%'}}><Typography
                sx={{fontSize: 12, fontWeight: 350}}
                gutterBottom>Flour</Typography></Divider>
        <Grid item container direction={'column'} alignItems={'flex-start'} marginTop={1} marginBottom={1}>
          <QuantityIngredientCollectionForm ingredientData={formData.flour} setIngredientData={updateFlourData}
                                            idField={'id'} nameField={'name'} unitField={'amount'}
                                            unitTypeField={'unit'} defaultUnitType={unitGrams} collectionName={'Flour'}
                                            excludeUnits={FLOUR_EXCLUSION_UNITS} minCollectionSize={1}/>
        </Grid>
        <Divider textAlign={'right'} sx={{marginTop: 2, width: '90%'}}><Typography
                sx={{fontSize: 12, fontWeight: 350}}
                gutterBottom>Liquid</Typography></Divider>
        <Grid item container direction={'column'} alignItems={'flex-start'} marginTop={1} marginBottom={1}>
          <QuantityIngredientCollectionForm ingredientData={formData.liquid} setIngredientData={updateLiquidData}
                                            idField={'id'} nameField={'name'} unitField={'amount'}
                                            unitTypeField={'unit'} defaultUnitType={unitGrams} collectionName={'Liquid'}
                                            excludeUnits={LIQUID_EXCLUSION_UNITS} minCollectionSize={1}/>
        </Grid>
          <Divider textAlign={'right'} sx={{marginTop: 2, width: '90%'}}><Typography
                  sx={{fontSize: 12, fontWeight: 350}}
                  gutterBottom>Inclusions</Typography></Divider>
          <Grid item container direction={'column'} alignItems={'flex-start'} marginTop={1} marginBottom={1}>
            <QuantityIngredientCollectionForm ingredientData={formData.mixin} setIngredientData={updateMixinData}
                                              idField={'id'} nameField={'name'} unitField={'amount'}
                                              unitTypeField={'unit'} defaultUnitType={unitGrams} collectionName={'Inclusion'}
                                              excludeUnits={GENERAL_DRY_EXCLUSION_UNITS} minCollectionSize={0}/>
          </Grid>
      </Grid>) : null}
    </div>
  )
}

export {
  WeightPrefermentForm
}